import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Check, FileUp, UserPlus } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import { CenterType, useCenterInvitations, useCenterSeats, useRevokeInvitation } from "../../../center";
import InviteMemberModal from "./InviteMemberModal";
import InviteMembersCsvModal from "./InviteMembersCsvModal";

interface Props {
    centerId: string;
    type: CenterType;
}

const STATE_STYLES: Record<string, string> = {
    pending: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400",
    accepted: "bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400",
    revoked: "bg-gray-100 text-gray-600 dark:bg-gray-500/10 dark:text-gray-400",
    expired: "bg-gray-100 text-gray-600 dark:bg-gray-500/10 dark:text-gray-400",
};

/**
 * Inviting people into the centre, and seeing who has been invited.
 *
 * The join link is shown next to every live invitation rather than hidden
 * behind the email that carried it. Delivery is best-effort — it bounces, it
 * lands in spam, the address has a typo — and an administrator who cannot see
 * the link has no way to recover from any of that.
 *
 * Inviting itself opens in a dialog from the button at the top, so the list of
 * who has been asked is what the card is about.
 *
 * There is no re-send. Revoking and inviting again produces a fresh token,
 * which is the safer shape: re-sending would extend the life of a link that
 * may already have gone astray.
 */
export default function CenterInvitationsCard({ centerId, type }: Props) {
    const { t } = useTranslation();
    const { invitations, loading } = useCenterInvitations(centerId);
    const { seats } = useCenterSeats(centerId);
    const revoke = useRevokeInvitation(centerId);

    const [inviting, setInviting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [copied, setCopied] = useState<string | null>(null);

    // Unknown seats (still loading, or the count failed) do not block: the
    // server refuses a full centre anyway, and says so.
    const isFull = seats !== null && seats.available === 0;

    const handleCopy = async (id: string, url: string) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(id);
        } catch {
            // A browser that refuses clipboard access is not an error worth a
            // banner: the link is on screen and can be selected by hand.
            setCopied(null);
        }
    };

    const roleLabel = (role: string) =>
        role === "admin"
            ? t("center.roles.admin")
            : type === "training"
              ? t("center.roles.trainer")
              : t("center.roles.therapist");

    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="mb-1 text-lg font-semibold text-content-primary">
                        {t("center.invitations.title")}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t("center.invitations.listSubtitle")}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button
                        variant="outline"
                        onClick={() => setImporting(true)}
                        disabled={isFull}
                        startIcon={<FileUp size={16} aria-hidden="true" />}
                    >
                        {t("center.csv.mentors.button")}
                    </Button>
                    <Button
                        onClick={() => setInviting(true)}
                        disabled={isFull}
                        startIcon={<UserPlus size={16} aria-hidden="true" />}
                    >
                        {t("center.invitations.inviteButton")}
                    </Button>
                </div>
            </div>

            {seats && (
                <div
                    className={`mb-5 rounded-xl border px-4 py-3 text-sm ${
                        isFull
                            ? "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300"
                            : "border-gray-200 bg-gray-50 text-content-primary dark:border-gray-800 dark:bg-white/[0.02]"
                    }`}
                >
                    <p className="font-medium">
                        {t("center.invitations.seatsUsed", {
                            used: seats.used,
                            seats: seats.seats,
                        })}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        {isFull
                            ? t("center.invitations.seatsFull")
                            : t("center.invitations.seatsHint")}
                    </p>
                </div>
            )}

            {loading && <p className="text-sm text-gray-500">{t("common.loading")}</p>}

            {!loading && invitations.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t("center.invitations.empty")}
                </p>
            )}

            {!loading && invitations.length > 0 && (
                <ul className="flex flex-col divide-y divide-gray-100 dark:divide-gray-800">
                    {invitations.map((invitation) => (
                        <li
                            key={invitation.id}
                            className="flex flex-wrap items-center gap-3 py-3 text-sm"
                        >
                            <span className="text-content-primary">{invitation.email}</span>
                            <span className="flex flex-wrap gap-1">
                                {invitation.roles.map((role) => (
                                    <span
                                        key={role}
                                        className="rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700 dark:bg-brand-500/10 dark:text-brand-400"
                                    >
                                        {roleLabel(role)}
                                    </span>
                                ))}
                            </span>
                            <span
                                className={`rounded-full px-2 py-0.5 text-xs ${
                                    STATE_STYLES[invitation.state] ?? STATE_STYLES.revoked
                                }`}
                            >
                                {t(`center.invitations.state.${invitation.state}`)}
                            </span>

                            {invitation.state === "pending" && (
                                <span className="ml-auto flex items-center gap-2">
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        startIcon={
                                            copied === invitation.id ? (
                                                <Check size={14} />
                                            ) : (
                                                <Copy size={14} />
                                            )
                                        }
                                        onClick={() =>
                                            handleCopy(invitation.id, invitation.join_url)
                                        }
                                    >
                                        {copied === invitation.id
                                            ? t("center.invitations.copied")
                                            : t("center.invitations.copyLink")}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => revoke.mutate(invitation.id)}
                                        disabled={revoke.isPending}
                                    >
                                        {t("center.invitations.revoke")}
                                    </Button>
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            <InviteMembersCsvModal
                isOpen={importing}
                onClose={() => setImporting(false)}
                centerId={centerId}
                type={type}
            />
            <InviteMemberModal
                isOpen={inviting}
                onClose={() => setInviting(false)}
                centerId={centerId}
                type={type}
            />
        </section>
    );
}
